import type { ReactNode } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

export type TransactionListTotalsBarMode = 'selected' | 'page';

export interface TransactionListTotalsBarProps {
  mode: TransactionListTotalsBarMode;
  count: number;
  totalAmount: number;
  balance: number;
  hideBalance?: boolean;
  pageCountSuffix?: string;
  onClear?: () => void;
  children?: ReactNode;
}

const TransactionListTotalsBar = ({
  mode,
  count,
  totalAmount,
  balance,
  hideBalance = false,
  pageCountSuffix = '',
  onClear,
  children,
}: TransactionListTotalsBarProps) => {
  const showBalance = !hideBalance && balance > 0;
  const countLabel =
    mode === 'selected'
      ? `${count} selected`
      : `Totals (${count}${pageCountSuffix})`;

  return (
    <div className="z-20 flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="font-medium text-gray-900">{countLabel}</span>
        <span className="text-gray-600">
          Total{' '}
          <span className="font-medium text-gray-900">{formatCurrency(totalAmount)}</span>
        </span>
        {hideBalance ? null : showBalance ? (
          <span className="text-gray-600">
            Balance{' '}
            <span
              className={`font-medium ${mode === 'page' && balance <= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              {formatCurrency(balance)}
            </span>
          </span>
        ) : (
          <span className="text-gray-600">
            Balance <span className="font-medium text-gray-400">—</span>
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {children}
        {mode === 'selected' && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-slate-700 underline hover:text-slate-900"
          >
            Clear selection
          </button>
        )}
      </div>
    </div>
  );
};

export default TransactionListTotalsBar;
